// components/auth/SharedLoginScreen.js
// Single login surface used by both /drivers (DriverApp) and /haulers
// (HaulerApp). Extracted from the inline login that previously lived
// in DriverApp.js so the two surfaces stay byte-equivalent.
//
// Renders three flows internally:
//   - Reset password (when resetMode prop is true) — driven by the parent's
//     PASSWORD_RECOVERY auth listener.
//   - Forgot password (forgot link sends a reset email).
//   - Login (default).
//
// The only behavioral difference between surfaces is the redirectTo URL
// for password reset — a Hauler reset link returns to /haulers, a Driver
// reset link returns to /drivers. The visual contract is identical.
//
// Original IP — LoadDash Services LLC

import React, { useEffect, useState } from "react";
import { supabase, signInWithGoogle } from "../../services/supabaseService";
import { BRAND_NAME } from "../../constants";
import { EVENTS, trackRoleContextEvent } from "../../analytics/roleContextTelemetry";

const SORA = `@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@500;600;700&display=swap');`;

function redirectFor(surface) {
  return surface === "hauler"
    ? "https://loaddashservices.com/haulers"
    : "https://loaddashservices.com/drivers";
}

export default function SharedLoginScreen({
  surface = "driver",
  resetMode = false,
  onResetComplete,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  useEffect(() => {
    if (surface !== "hauler") return;
    trackRoleContextEvent(EVENTS.HAULER_INTEREST, null, {
      intent: "hauler_interest",
      role_context: "hauler",
      surface: "hauler_login",
      page: "hauler_login",
    });
  }, [surface]);

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError("");
    setLoginBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setLoginError(error.message);
    } catch (err) {
      setLoginError(err.message || "Login failed");
    }
    setLoginBusy(false);
  }

  async function handleGoogleSignIn() {
    setLoginError("");
    setLoginBusy(true);
    try {
      const { error } = await signInWithGoogle("https://loaddashservices.com/haulers");
      if (error) { setLoginError(error.message); setLoginBusy(false); }
      // On success the browser redirects to Google; no further state needed.
    } catch (err) {
      setLoginError(err.message || "Sign-in failed");
      setLoginBusy(false);
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    if (!email.trim()) { setLoginError("Enter your email address"); return; }
    setForgotBusy(true);
    setLoginError("");
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectFor(surface),
      });
    } catch (_) { /* suppress — never reveal whether email exists */ }
    setForgotSent(true);
    setForgotBusy(false);
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setResetError("");
    if (newPassword.length < 8) { setResetError("Password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { setResetError("Passwords do not match"); return; }
    setResetBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword("");
      setConfirmPassword("");
      onResetComplete && onResetComplete();
    } catch (err) {
      const msg = (err.message || "").toLowerCase();
      if (msg.includes("expired") || msg.includes("invalid")) {
        setResetError("This link has expired. Request a new one.");
      } else {
        setResetError(err.message || "Reset failed");
      }
    }
    setResetBusy(false);
  }

  // ── Reset password flow (deep-link recovery) ───────────
  if (resetMode) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "linear-gradient(160deg,#060e1a,#0a1628)", display: "flex", flexDirection: "column", fontFamily: "'Sora',system-ui,sans-serif", overflow: "hidden" }}>
        <style>{SORA}</style>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%" }}>
          <img src="/Loaddashbutton.png" alt={BRAND_NAME} style={{ width: 80, height: 80, borderRadius: 18, marginBottom: 14, objectFit: "contain" }} />
          <div style={{ fontWeight: 900, fontSize: 24, color: "#fff", marginBottom: 4 }}>Reset Password</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#2d5580", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 32 }}>Choose a new password</div>
        </div>
        <div style={{ width: "100%", background: "#0d1b2e", borderTop: "1px solid #1e3558", borderRadius: "20px 20px 0 0", padding: "24px max(20px, env(safe-area-inset-right, 0px)) calc(36px + env(safe-area-inset-bottom, 0px)) max(20px, env(safe-area-inset-left, 0px))", boxSizing: "border-box" }}>
          <form onSubmit={handleResetPassword}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#2d5580", letterSpacing: 2, textTransform: "uppercase", marginBottom: 7 }}>New Password</div>
            <div style={{ background: "#060e1a", border: "1.5px solid #1e3558", borderRadius: 11, padding: "13px 14px", marginBottom: 16 }}>
              <input value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" type="password" style={{ width: "100%", fontSize: 13, color: "#8fadc8", background: "transparent", border: "none", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#2d5580", letterSpacing: 2, textTransform: "uppercase", marginBottom: 7 }}>Confirm Password</div>
            <div style={{ background: "#060e1a", border: "1.5px solid #1e3558", borderRadius: 11, padding: "13px 14px", marginBottom: 20 }}>
              <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" type="password" style={{ width: "100%", fontSize: 13, color: "#8fadc8", background: "transparent", border: "none", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
            <button type="submit" disabled={resetBusy} style={{ width: "100%", padding: 16, borderRadius: 13, border: "none", background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: resetBusy ? "wait" : "pointer", fontFamily: "inherit", boxShadow: "0 4px 18px rgba(21,101,192,.45)", opacity: resetBusy ? 0.7 : 1 }}>{resetBusy ? "Updating..." : "Update Password"}</button>
            {resetError && <div style={{ fontSize: 12, color: "#ef5350", textAlign: "center", marginTop: 8 }}>{resetError}</div>}
          </form>
        </div>
      </div>
    );
  }

  // ── Login + forgot password flow ───────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, background: "linear-gradient(160deg,#060e1a,#0a1628)", display: "flex", flexDirection: "column", fontFamily: "'Sora',system-ui,sans-serif", overflow: "hidden" }}>
      <style>{SORA}</style>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", paddingTop: "env(safe-area-inset-top, 24px)", padding: "24px 20px" }}>
        <img src="/Loaddashbutton.png" alt={BRAND_NAME} style={{ width: 80, height: 80, borderRadius: 18, marginBottom: 14, objectFit: "contain" }} />
        <div style={{ fontWeight: 900, fontSize: 24, color: "#fff", marginBottom: 4 }}>{BRAND_NAME} Haulers</div>
        {surface === "hauler" ? (
          <>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#42a5f5", letterSpacing: 2, textTransform: "uppercase", marginBottom: 14 }}>
              {BRAND_NAME} Haulers · Hill AFB
            </div>
            <div style={{ fontSize: 13, color: "#8fadc8", textAlign: "center", maxWidth: 320, lineHeight: 1.5, marginBottom: 24 }}>
              Sign in to view today's available jobs and start earning.
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#2d5580", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14 }}>Secure Access</div>
            <div style={{ fontSize: 13, color: "#8fadc8", textAlign: "center", maxWidth: 320, lineHeight: 1.5, marginBottom: 24 }}>
              Sign in to your driver account.
            </div>
          </>
        )}
      </div>
      <div style={{ width: "100%", background: "#0d1b2e", borderTop: "1px solid #1e3558", borderRadius: "20px 20px 0 0", padding: "24px max(20px, env(safe-area-inset-right, 0px)) calc(36px + env(safe-area-inset-bottom, 0px)) max(20px, env(safe-area-inset-left, 0px))", boxSizing: "border-box" }}>
        {forgotSent ? (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Check your email for a reset link</div>
            <div style={{ fontSize: 12, color: "#4a7aaa", marginBottom: 16 }}>If an account exists, you will receive an email shortly.</div>
            <span onClick={() => { setForgotMode(false); setForgotSent(false); setLoginError(""); }} style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", cursor: "pointer" }}>Back to login</span>
          </div>
        ) : forgotMode ? (
          <form onSubmit={handleForgotPassword}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#2d5580", letterSpacing: 2, textTransform: "uppercase", marginBottom: 7 }}>Email Address</div>
            <div style={{ background: "#060e1a", border: "1.5px solid #1e3558", borderRadius: 11, padding: "13px 14px", marginBottom: 20 }}>
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="driver@loaddashservices.com" type="email" style={{ width: "100%", fontSize: 13, color: "#8fadc8", background: "transparent", border: "none", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
            <button type="submit" disabled={forgotBusy} style={{ width: "100%", padding: 16, borderRadius: 13, border: "none", background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: forgotBusy ? "wait" : "pointer", fontFamily: "inherit", boxShadow: "0 4px 18px rgba(21,101,192,.45)", opacity: forgotBusy ? 0.7 : 1 }}>{forgotBusy ? "Sending..." : "Send reset link"}</button>
            {loginError && <div style={{ fontSize: 12, color: "#ef5350", textAlign: "center", marginTop: 8 }}>{loginError}</div>}
            <div style={{ textAlign: "right", marginTop: 10 }}>
              <span onClick={() => { setForgotMode(false); setLoginError(""); }} style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", cursor: "pointer" }}>Back to login</span>
            </div>
          </form>
        ) : (
        <form onSubmit={handleLogin}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#2d5580", letterSpacing: 2, textTransform: "uppercase", marginBottom: 7 }}>Hauler ID</div>
          <div style={{ background: "#060e1a", border: "1.5px solid #1e3558", borderRadius: 11, padding: "13px 14px", marginBottom: 16 }}>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="driver@loaddashservices.com" type="email" style={{ width: "100%", fontSize: 13, color: "#8fadc8", background: "transparent", border: "none", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#2d5580", letterSpacing: 2, textTransform: "uppercase", marginBottom: 7 }}>Access Code</div>
          <div style={{ background: "#060e1a", border: "1.5px solid #1e3558", borderRadius: 11, padding: "13px 14px", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
            <input value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" type={showPassword ? "text" : "password"} style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#8fadc8", background: "transparent", border: "none", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? "Hide access code" : "Show access code"} style={{ border: "none", background: "transparent", color: "#42a5f5", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", padding: 0, flexShrink: 0 }}>
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <div style={{ textAlign: "right", marginBottom: 16 }}>
            <span onClick={() => { setForgotMode(true); setLoginError(""); }} style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", cursor: "pointer" }}>Forgot password?</span>
          </div>
          <button type="submit" disabled={loginBusy} style={{ width: "100%", padding: 16, borderRadius: 13, border: "none", background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: loginBusy ? "wait" : "pointer", fontFamily: "inherit", boxShadow: "0 4px 18px rgba(21,101,192,.45)", opacity: loginBusy ? 0.7 : 1 }}>{loginBusy ? "Accessing Route..." : "Access Route"}</button>
          {loginError && <div style={{ fontSize: 12, color: "#ef5350", textAlign: "center", marginTop: 8 }}>{loginError}</div>}
        </form>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 16px" }}>
          <div style={{ flex: 1, height: 1, background: "#1e3558" }} />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, color: "#2d5580", textTransform: "uppercase" }}>Hauler Only</span>
          <div style={{ flex: 1, height: 1, background: "#1e3558" }} />
        </div>

        {surface === "hauler" && (
          <>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loginBusy}
              style={{
                width: "100%",
                minHeight: 44,
                padding: "13px",
                borderRadius: 13,
                border: "1.5px solid #1e3558",
                background: "#0d1b2e",
                color: "#fff",
                fontWeight: 800,
                fontSize: 15,
                cursor: loginBusy ? "wait" : "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <span aria-hidden="true" style={{ display: "inline-flex", width: 18, height: 18, borderRadius: "50%", background: "#fff", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, color: "#4285F4" }}>G</span>
              Continue with Google
            </button>
            <div style={{ textAlign: "center", fontSize: 11, color: "#4a7aaa", lineHeight: 1.5, marginBottom: 12 }}>
              For approved Haulers using the same Google email on file. New Haulers should apply first.
            </div>
            <a
              href="/haulers/apply"
              onClick={() => trackRoleContextEvent(EVENTS.HAULER_INTEREST, null, {
                intent: "hauler_interest",
                role_context: "hauler",
                surface: "hauler_login",
                page: "hauler_login",
                action: "apply_link_clicked",
              })}
              style={{
                display: "block",
                textAlign: "center",
                fontSize: 13,
                fontWeight: 700,
                color: "#42a5f5",
                textDecoration: "none",
                padding: "10px 0",
                marginBottom: 4,
              }}
            >
              New here? Apply to become a Hauler →
            </a>
            <div style={{ textAlign: "center", fontSize: 11, color: "#2d5580", marginBottom: 12 }}>
              Serving Boyer Hill Military Housing residents
            </div>
          </>
        )}

        <div style={{ textAlign: "center", fontSize: 11 }}>
          <span style={{ color: "#2d5580" }}>Resident portal: </span>
          <span style={{ color: "#42a5f5", fontWeight: 700 }}>loaddashservices.com</span>
        </div>
      </div>
    </div>
  );
}
